import React from "react";
import {Select} from "../../form/select/index";
import {FormGroup} from "../../form/group/index";
import {Input} from "../../form/input/index";
import {Button} from "../../button/index";
import { FaBars, FaTrashAlt, FaCopy } from "react-icons/fa";
import InputMask from "react-input-mask";
import {InputBox} from "../../form/input.box/index";
import {Tooltip} from "../../tooltip/index";
import {Checkbox} from "../../form/checkbox";
import {arrayMove, SortableContainer, SortableElement, SortableHandle} from "react-sortable-hoc";

const Days = [
  { label: "Monday", value: "Monday" },
  { label: "Tuesday", value: "Tuesday" },
  { label: "Wednesday", value: "Wednesday" },
  { label: "Thursday", value: "Thursday" },
  { label: "Friday", value: "Friday" },
  { label: "Saturday", value: "Saturday" },
  { label: "Sunday", value: "Sunday" },
];

export interface ComponentProps {
  hours: ListType;
  onChange: (h: ListType) => void;
  allowNone?: boolean;
}

type ItemType = BusinessHoursSlot;
type ListType = ItemType[];
type HandleChange = (i: number, key: keyof ItemType, value: string | boolean) => void;
type HandleCopy = (i: number) => void;
type HandleRemove = (i: number) => void;
type HandleSort = (data: { oldIndex: number; newIndex: number }) => void;

interface ListProps {
  items: ListType;
  change: HandleChange;
  remove: HandleRemove;
  copy: HandleCopy;
  allowNone: boolean;
}
interface ItemProps {
  item: ItemType;
  itemIndex: number;
  itemsLength: number;
  change: HandleChange;
  remove: HandleRemove;
  copy: HandleCopy;
  allowNone: boolean;
}

const DragHandle = SortableHandle(() => {
  return (
    <InputBox className="no-round width40 cursor-move flex-center">
      <p className="big font-bold">
        <FaBars/>
      </p>
    </InputBox>
  );
});

const SortableItem = SortableElement((props: ItemProps) => {
  const { itemIndex, itemsLength, change, copy, remove, allowNone } = props;
  const h = props.item;
  return (
    <div key={itemIndex} className="flex-line" style={{ alignItems: "flex-end" }}>

      <DragHandle/>

      <div className="flex-line" style={{ alignItems: "flex-end" }}>
        <FormGroup
          className="m-b-0"
          no_border={true}>
          <Select
            className="no-round"
            options={Days}
            style={{ width: "120px" }}
            value={h.day}
            onChange={(e) => change(itemIndex, "day", e.target.value)}
            required={true}/>
        </FormGroup>

        <FormGroup
          className="m-b-0"
          no_border={true}>
          <InputMask mask="99:99" value={h.open} onChange={(e) => change(itemIndex, "open", e.target.value)} disabled={h.h24}>
            {(inputProps: any) => <Input type="text" className="no-round" style={{ width: "100px" }} placeholder="00:00" {...inputProps} disabled={h.h24}/>}
          </InputMask>
        </FormGroup>

        <FormGroup
          className="m-b-0"
          no_border={true}>
          <InputMask mask="99:99" value={h.close} onChange={(e) => change(itemIndex, "close", e.target.value)} disabled={h.h24}>
            {(inputProps: any) => <Input type="text" className="no-round" style={{ width: "100px" }} placeholder="00:00" {...inputProps} disabled={h.h24}/>}
          </InputMask>
        </FormGroup>

        <FormGroup
          className="m-b-0"
          no_border={true}>
          <InputBox className="no-round flex-center width75">
            <Checkbox
              id={`${itemIndex}-24-hours-checkbox`}
              checked={h.h24}
              onChange={(e) => change(itemIndex, "h24", e.target.checked)}/>
          </InputBox>
        </FormGroup>
      </div>

      { (allowNone || itemsLength !== 1) &&
      <Tooltip text="Delete" width={65} position="top" disable={itemIndex !== 0}>
        <Button type="button" className="no-round" paddinglr={5} color="white" onClick={() => remove(itemIndex)}>
          <FaTrashAlt/>
        </Button>
      </Tooltip>
      }

      <Tooltip text="Copy" width={65} position="top" disable={itemIndex !== 0}>
        <Button type="button" className="no-round" paddinglr={5} color="white" onClick={() => copy(itemIndex)}>
          <FaCopy/>
        </Button>
      </Tooltip>

    </div>
  );
});

const SortableItemContainer = SortableContainer((props: ListProps) => {
  return (
    <div>
      {props.items.map((item, i) => (
        <SortableItem
          key={`item-${i}`}
          index={i}
          item={item}
          itemIndex={i}
          itemsLength={props.items.length}
          change={props.change}
          remove={props.remove}
          copy={props.copy}
          allowNone={props.allowNone}
        />
      ))}
    </div>
  );
});

export class FieldOpeningHours extends React.Component<ComponentProps, {}> {

  add = () => {
    const hours = [ ...this.props.hours ];
    hours.push({
      day: "Monday",
      open: "09:00",
      close: "21:00",
      h24: false,
    });
    this.props.onChange(hours);
  }

  copy = (i: number) => {
    const hours = [ ...this.props.hours ];
    const hour = { ...hours[i] };
    hours.push(hour);
    this.props.onChange(hours);
  }

  remove = (i: number) => {
    const hours = ([ ...this.props.hours ]);
    hours.splice(i, 1);
    this.props.onChange(hours);
  }

  change = (i: number, key: keyof BusinessHoursSlot, value: string | boolean) => {
    const hours = [ ...this.props.hours ];
    hours[i][key] = value;
    this.props.onChange(hours);
  }

  onSortEnd: HandleSort = ({oldIndex, newIndex}) => {
    const { onChange, hours } = this.props;
    onChange(arrayMove(hours, oldIndex, newIndex));
  }

  render() {
    const { hours, allowNone } = this.props;
    if (hours.length === 0) {
      return (
        <Button color="primary-inverse" size="xs" type="button" onClick={this.add}>
          Add Time Slot
        </Button>
      );
    }
    return (
      <div>

        <Button type="button" size="xs" color="primary-inverse" className="m-b-3" onClick={this.add}>
          Add Time Slot
        </Button>

        <div className="flex-line" style={{ alignItems: "flex-end" }}>

          <div style={{width: "40px", minWidth: "40px"}}/>

          <div className="flex-line" style={{ alignItems: "flex-end" }}>

            <FormGroup
              style={{ width: "120px" }}
              className="m-b-0"
              title={"Day"}
              small_title={true}
              no_border={true}/>

            <FormGroup
              style={{ width: "100px" }}
              className="m-b-0"
              title={"Open"}
              small_title={true}
              no_border={true}/>

            <FormGroup
              style={{ width: "100px" }}
              className="m-b-0"
              title={"Close"}
              small_title={true}
              no_border={true}/>

            <FormGroup
              style={{ width: "75px" }}
              className="m-b-0"
              title={"24 Hour"}
              small_title={true}
              no_border={true}/>

          </div>

        </div>

        <SortableItemContainer
          helperClass="zindex-100 bsl-2"
          pressDelay={50}
          useDragHandle={true}
          items={hours}
          change={this.change}
          copy={this.copy}
          remove={this.remove}
          onSortEnd={this.onSortEnd}
          allowNone={allowNone || false}
        />

      </div>
    );
  }

}
